import { initComponentStore } from "@/utils/mobx";


export interface SelectInputOption {
    title: string;
    value: string;
    isDisabled?: boolean;
    // Options sharing a group are listed under one header (searchable mode only); the closed state shows "group / title".
    group?: string | null;
    // Secondary text shown muted after the title and matched by search (e.g. the raw id).
    hint?: string;
}

export class SelectInputStore {
    value: string | null = null;
    options: SelectInputOption[] | null = null;
    isPopupVisible: boolean = false;
    onChange: ((value: string | null) => any) | null = null;
    className: string = "";
    placeholder: string = "";
    searchable: boolean = false;
    searchPlaceholder: string = "";
    // Offer the typed query itself as a value when nothing matches it exactly.
    allowCustom: boolean = false;
    customTitle: ((query: string) => string) | null = null;
    emptyText: string = "";

    query: string = "";
    focusedIndex: number = 0;

    constructor() {
        initComponentStore(this);
    }

    togglePopup() {
        if (this.isPopupVisible) {
            this.hidePopup();
        } else {
            this.query = "";
            this.focusedIndex = Math.max(0, this.filteredOptions.findIndex(o => o.value === this.value));
            this.isPopupVisible = true;
        }
    }

    hidePopup() {
        this.isPopupVisible = false;
    }

    changeValue(value: string | null) {
        if (this.onChange) {
            this.onChange(value);
        } else {
            this.value = value;
        }
        this.isPopupVisible = false;
    }

    setQuery(query: string) {
        this.query = query;
        this.focusedIndex = 0;
    }

    get filteredOptions(): SelectInputOption[] {
        const options = this.options ?? [];
        // Every whitespace-separated token must be a substring, so "deep pro" finds "deepseek/deepseek-v4-pro".
        const tokens = this.query.toLowerCase().split(/\s+/).filter(Boolean);
        const matched = tokens.length === 0 ? options : options.filter(o => {
            const haystack = `${o.title} ${o.value} ${o.group ?? ""} ${o.hint ?? ""}`.toLowerCase();
            return tokens.every(t => haystack.includes(t));
        });
        const query = this.query.trim();
        if (this.allowCustom && query && !options.some(o => o.value === query)) {
            const title = this.customTitle ? this.customTitle(query) : query;
            return [...matched, { title, value: query }];
        }
        return matched;
    }

    keyDown(e: React.KeyboardEvent) {
        const count = this.filteredOptions.length;
        if (e.key === "ArrowDown") {
            this.focusedIndex = Math.min(this.focusedIndex + 1, count - 1);
        } else if (e.key === "ArrowUp") {
            this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
        } else if (e.key === "Enter") {
            const option = this.filteredOptions[this.focusedIndex];
            if (option && !option.isDisabled) this.changeValue(option.value);
        } else if (e.key === "Escape") {
            this.hidePopup();
        } else {
            return;
        }
        e.preventDefault();
        // Keep Escape/Enter from also closing or submitting the surrounding dialog.
        e.stopPropagation();
    }

    focusOption(index: number) {
        this.focusedIndex = index;
    }

    getTitle(value: string | null): string {
        const option = (this.options ?? []).find(o => o.value === value);
        if (!option) return value ?? "";
        // Closed state has no group header, so keep the group to tell same-named options apart.
        return option.group ? `${option.group} / ${option.title}` : option.title;
    }
}
